import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./components/theme-provider";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PlatformBillingPage from "./pages/PlatformBillingPage";
import WorkspaceAccessPage from "./pages/WorkspaceAccessPage";
import SetupPage from "./pages/SetupPage";
import DashboardPage from "./pages/DashboardPage";
import POSPage from "./pages/POSPage";
import SalesHistoryPage from "./pages/SalesHistoryPage";
import RefundsPage from "./pages/RefundsPage";
import InventoryPage from "./pages/InventoryPage";
import OperationsPage from "./pages/OperationsPage";
import ProductsPage from "./pages/ProductsPage";
import CustomersPage from "./pages/CustomersPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import LandingPage from "./pages/LandingPage";

// Layout
import MainLayout from "./components/MainLayout";

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4 animate-pulse">
        <span className="text-3xl font-bold text-primary-foreground">S</span>
      </div>
      <h1 className="font-heading text-2xl font-bold text-primary">SurviPOS</h1>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading, isSetupComplete, workspaceAccessError } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'owner' && user.email_verified === false) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email)}`} replace />;
  }

  if (workspaceAccessError?.status === 402 || workspaceAccessError?.status === 403) {
    return <Navigate to="/payment" replace />;
  }

  // Redirect to setup if business setup is not complete (only for owners)
  if (user.role === 'owner' && !isSetupComplete) {
    return <Navigate to="/setup" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
};

// Public Route component (redirect to home if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading, isSetupComplete, workspaceAccessError } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    if (user.role === 'owner' && user.email_verified === false) {
      return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email)}`} replace />;
    }
    if (workspaceAccessError?.status === 402 || workspaceAccessError?.status === 403) {
      return <Navigate to="/payment" replace />;
    }
    if (user.role === 'owner' && !isSetupComplete) {
      return <Navigate to="/setup" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Setup Route (requires auth but no setup)
const SetupRoute = ({ children }) => {
  const { user, loading, isSetupComplete, workspaceAccessError } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'owner' && user.email_verified === false) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email)}`} replace />;
  }

  if (workspaceAccessError?.status === 402 || workspaceAccessError?.status === 403) {
    return <Navigate to="/payment" replace />;
  }

  if (isSetupComplete) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/payment" element={<WorkspaceAccessPage />} />
      <Route path="/workspace-access" element={<Navigate to="/payment" replace />} />
      <Route path="/platform-billing" element={<PlatformBillingPage />} />

      {/* Setup Route */}
      <Route
        path="/setup"
        element={
          <SetupRoute>
            <SetupPage />
          </SetupRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pos"
        element={
          <ProtectedRoute>
            <POSPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-history"
        element={
          <ProtectedRoute>
            <SalesHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <ProductsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <InventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations"
        element={
          <ProtectedRoute>
            <OperationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/refunds"
        element={
          <ProtectedRoute>
            <RefundsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <CustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
